import type { RegistryAllocation } from '../../api/registryEntityTypes';
import type { Application, RegistryState } from '../../data/types';
import { allocationKey } from '../../entities/entitySource';
import { newId } from '../../lib/random';
import { today, withAudit } from '../helpers';

const CLOSED: Application['status'][] = ['REJECTED', 'WITHDRAWN'];

/** The live (not rejected or withdrawn) application already based on this allocation, if any. */
export function applicationForAllocation(
  applications: Application[],
  allocationId: string,
): Application | undefined {
  return applications.find(
    (application) =>
      application.allocationId === allocationId && !CLOSED.includes(application.status),
  );
}

/**
 * Invites the allocation's entity to apply under a sample offer. The allocation may be real (from
 * the admin API) while the application stays in the mock store until applications are built.
 */
export function startApplicationFromAllocation(
  state: RegistryState,
  action: { allocation: RegistryAllocation; offerId: string; email?: string },
): RegistryState {
  const { allocation } = action;
  const allocationId = allocationKey(allocation);
  const offer = state.offers.find((item) => item.id === action.offerId);
  if (!offer || applicationForAllocation(state.applications, allocationId)) return state;
  const reference = `APP-26-${1100 + state.applications.length}`;
  const application: Application = {
    id: newId('app'),
    reference,
    fundId: offer.fundId,
    classId: offer.classId,
    investmentEntityId: allocation.investmentEntityExternalId,
    allocationId,
    applicantName: allocation.investmentEntityName,
    applicantType: allocation.investmentEntityType,
    contactName: allocation.contact.name,
    email: action.email ?? '',
    amount: Number(allocation.allocatedAmount),
    status: 'INVITED',
    createdAt: today,
    checklist: {
      entity: 'PENDING',
      owners: 'PENDING',
      identity: 'PENDING',
      tax: 'PENDING',
      bank: 'PENDING',
      wholesale: 'PENDING',
      signed: 'PENDING',
      funds: 'PENDING',
    },
    notes: [`Started from the ${allocation.fundOpportunity.name} allocation`],
  };
  return withAudit(
    { ...state, applications: [application, ...state.applications] },
    'Application started from allocation',
    reference,
    `${allocation.investmentEntityName} · ${offer.title}`,
  );
}
